/**
 * NaraShape recipe detail/edit page for /admin/document-recipes/:id route.
 * Allows viewing and editing recipe details including Typst code.
 */

import { useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminRoutes, useWorkspaceUrl } from "../lib/routes";
import { PageHeader } from "../components/layout/PageHeader";
import {
  getShapeRecipe,
  publishShapeRecipe,
  activateShapeRecipeVersion,
  createShapeRecipe,
  updateShapeRecipe,
  deleteShapeRecipe,
  validateShapeRecipeCode,
  type ShapeRecipeVersionsResponse,
  type DocumentDefinition,
  type DocumentDefinitionSection,
  adminExtractDocumentStructure,
  getProjects,
  getProjectDocuments,
} from "../services/naraDoor";
import {
  FileText,
  ArrowLeft,
  Pencil,
  Save,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Layers,
  Trash2,
  Copy,
  Play,
  History,
  ChevronUp,
  ChevronDown,
  Tag,
  Eye,
  RotateCcw,
  MoreVertical,
  Search,
  Download,
  BookOpen,
  ChevronRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProfile } from "../contexts/ProfileContext";
import { getCurrentWorkspaceId } from "../lib/workspaceStore";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { ProductSchemaSection } from "../components/ProductSchemaSection";
import { RecipeModeBadge } from "../components/RecipeModeBadge";
import { STARTER_PRODUCT_SCHEMA, extractionRefusal, parseProductSchema } from "../lib/productSchema";
import { useShapeRecipeDraft, useShapeRecipeVersions, useShapeRecipeVersion } from "../hooks/useShapeRecipes";

/** Typst code is stored as base64 of UTF-8; text that is not base64 is shown as is. */
function decodeBase64Utf8(code: string): string {
  if (!code) return code;
  try {
    return new TextDecoder("utf-8").decode(Uint8Array.from(atob(code), (c) => c.charCodeAt(0)));
  } catch {
    return code;
  }
}

function SectionPreview({ section, depth = 0 }: { section: DocumentDefinitionSection; depth?: number }) {
  return (
    <div className={`border-l-2 border-border pl-3 py-1 ${depth > 0 ? "ml-3" : ""}`}>
      <div className="flex items-center gap-2">
        <ChevronRight className="h-3 w-3 text-muted-foreground" />
        <span className="text-sm font-medium">{section.name}</span>
        {section.repeatable && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400">
            repeatable
          </span>
        )}
      </div>
      {section.expected_content && (
        <p className="text-xs text-muted-foreground ml-5">{section.expected_content}</p>
      )}
      {section.fields && section.fields.length > 0 && (
        <div className="ml-5 mt-1 space-y-0.5">
          {section.fields.map((field, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-foreground">{field.name}</span>
              <span className="text-muted-foreground">({field.type})</span>
              {field.description && (
                <span className="text-muted-foreground">— {field.description}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {section.subsections && section.subsections.map((sub, i) => (
        <SectionPreview key={i} section={sub as DocumentDefinitionSection} depth={depth + 1} />
      ))}
    </div>
  );
}

export function NaraShapeTemplate() {
  const { t } = useTranslation();
  const { id: templateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const urls = useWorkspaceUrl();

  const { formatDate } = useDateFormatter();
  // Server data is read from the query, never copied into state: a refetch always shows.
  const draftQuery = useShapeRecipeDraft(templateId);
  const templateDetails = draftQuery.data ?? null;
  const isLoading = draftQuery.isLoading;
  const error = draftQuery.error ? draftQuery.error.message || "Failed to load template" : null;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Version state
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const versionsQuery = useShapeRecipeVersions(templateId, historyExpanded);
  const versions = versionsQuery.data?.versions ?? [];
  const versionsLoading = versionsQuery.isLoading;
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const versionQuery = useShapeRecipeVersion(templateId, viewingVersion);
  const viewingVersionData = versionQuery.data ?? null;
  const viewingVersionLoading = versionQuery.isLoading;
  const [confirmActivateVersion, setConfirmActivateVersion] = useState<number | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  // Reference structure state
  const [refExpanded, setRefExpanded] = useState(false);
  const [refProjectId, setRefProjectId] = useState<string | null>(null);
  const [refProjects, setRefProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [refProjectsLoading, setRefProjectsLoading] = useState(false);
  const [refDocuments, setRefDocuments] = useState<Array<{ id: string; name: string; files?: Array<{ id: string; filename?: string; mime_type?: string }> }>>([]);
  const [refDocsLoading, setRefDocsLoading] = useState(false);
  const [extractingDocId, setExtractingDocId] = useState<string | null>(null);
  const [extractedDefinition, setExtractedDefinition] = useState<DocumentDefinition | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [isApplyingSchema, setIsApplyingSchema] = useState(false);

  // Edit form state
  const [editLabel, setEditLabel] = useState("");
  const [editInputMode, setEditInputMode] = useState("transcription");
  const [editDescription, setEditDescription] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [editLocale, setEditLocale] = useState("");
  // Extraction recipes: the product schema as JSON text, and why it cannot be saved (an i18n key).
  const [editSchema, setEditSchema] = useState("");
  const [schemaErrorKey, setSchemaErrorKey] = useState<string | null>(null);

  const schemaText = (schema: unknown) => JSON.stringify(schema ?? STARTER_PRODUCT_SCHEMA, null, 2);

  // The form is a draft: it starts from the server data shown when Edit is clicked and is
  // never overwritten by a refetch while the admin is typing.
  const startEditing = () => {
    setEditLabel(templateDetails?.name || "");
    setEditInputMode(templateDetails?.input_mode || "transcription");
    setEditDescription(templateDetails?.description || "");
    setEditCode(decodeBase64Utf8(templateDetails?.code || ""));
    setEditPrompt(templateDetails?.prompt || "");
    setEditLocale(templateDetails?.locale || "");
    setEditSchema(schemaText(templateDetails?.input_mode === "extraction" ? templateDetails.schema : undefined));
    setSchemaErrorKey(null);
    setIsEditing(true);
  };

  // Validation state
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<{
    message: string;
    diagnostic?: string;
    is_data_error: boolean;
  } | null>(null);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  const handleValidate = async () => {
    const trimmedCode = editCode.trim();
    if (!trimmedCode) return;

    setIsValidating(true);
    setValidationError(null);
    setValidationSuccess(false);

    try {
      const encoder = new TextEncoder();
      const bytes = encoder.encode(trimmedCode);
      const binaryStr = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
      const encodedCode = btoa(binaryStr);

      const result = await validateShapeRecipeCode(
        encodedCode,
        (templateDetails?.schema as Record<string, unknown>) || undefined
      );

      if (result.valid) {
        setValidationSuccess(true);
        if (result.is_data_error && result.diagnostic) {
          setValidationError({
            message: result.message,
            diagnostic: result.diagnostic,
            is_data_error: true,
          });
        }
      } else {
        setValidationError({
          message: result.message,
          diagnostic: result.diagnostic,
          is_data_error: false,
        });
      }
    } catch (err: any) {
      setValidationError({
        message: err.message || t("naraShape.detail.errors.validationFailed"),
        diagnostic: err.details?.diagnostic,
        is_data_error: false,
      });
    } finally {
      setIsValidating(false);
    }
  };

  const [saveError, setSaveError] = useState<string | null>(null);

  // An extraction recipe holds a product schema and nothing that renders a file: nara-media refuses
  // the save otherwise, so the render fields are cleared in the same request.
  const handleSaveExtraction = async (name: string, description: string, prompt: string) => {
    const parsed = parseProductSchema(editSchema);
    if ("error" in parsed) {
      setSchemaErrorKey(parsed.error);
      return;
    }
    setSchemaErrorKey(null);
    setIsSaving(true);
    try {
      await updateShapeRecipe(templateId!, {
        name,
        input_mode: "extraction",
        description,
        prompt,
        locale: editLocale || undefined,
        schema: parsed.schema,
        code: "",
        template_html: "",
        gemini_sources: {},
        document_references: [],
      });
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to update extraction recipe", { templateId, error: err instanceof Error ? err.message : String(err) });
      if (extractionRefusal(err)) setSaveError(t("productSchema.errors.refused"));
      else setSaveError(t("naraShape.detail.errors.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    const trimmedLabel = editLabel.trim();
    const trimmedInputMode = editInputMode;
    const trimmedDescription = editDescription.trim();
    const trimmedCode = editCode.trim();
    const trimmedPrompt = editPrompt.trim();

    setSaveError(null);

    if (!trimmedLabel) {
      setSaveError(t("naraShape.detail.errors.nameRequired"));
      return;
    }

    if (trimmedInputMode === "extraction") {
      await handleSaveExtraction(trimmedLabel, trimmedDescription, trimmedPrompt);
      return;
    }

    if (!trimmedCode) {
      setSaveError(t("naraShape.detail.errors.codeRequired"));
      return;
    }

    setIsSaving(true);

    try {
      // Encode code as base64 with proper UTF-8 handling
      const encoder = new TextEncoder();
      const bytes = encoder.encode(trimmedCode);
      const binaryStr = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
      const encodedCode = btoa(binaryStr);

      await updateShapeRecipe(templateId!, {
        name: trimmedLabel,
        input_mode: trimmedInputMode,
        description: trimmedDescription,
        code: encodedCode,
        prompt: trimmedPrompt,
        locale: editLocale || undefined,
      });

      setIsEditing(false);
    } catch (err: any) {
      console.error("Failed to update template:", err);
      // Check for structured validation error from backend
      if (err.details?.type === "typst_validation_error" || err.details?.diagnostic) {
        setValidationError({
          message: err.message || t("naraShape.detail.errors.syntaxError"),
          diagnostic: err.details?.diagnostic,
          is_data_error: false,
        });
        setSaveError(t("naraShape.detail.errors.syntaxError"));
      } else {
        setSaveError(err.message || t("naraShape.detail.errors.saveFailed"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleDuplicate = async () => {
    if (!templateDetails) return;
    setIsDuplicating(true);
    try {
      const newTemplate = await createShapeRecipe({
        name: `${templateDetails.name} (${t("naraShape.detail.duplicate.copy")})`,
        input_mode: templateDetails.input_mode || "transcription",
        description: templateDetails.description || undefined,
        code: templateDetails.code || undefined,
        prompt: templateDetails.prompt || undefined,
        locale: templateDetails.locale || undefined,
        // An extraction recipe cannot be created without its product schema.
        ...(templateDetails.input_mode === "extraction" && { schema: templateDetails.schema as Record<string, unknown> | undefined }),
      });
      navigate(adminRoutes.documentRecipe(newTemplate.id));
    } catch (err: any) {
      console.error("Failed to duplicate template:", err);
      setSaveError(err.message || t("naraShape.detail.duplicate.failed"));
    } finally {
      setIsDuplicating(false);
    }
  };

  const handlePublish = async () => {
    if (!templateId) return;
    setIsPublishing(true);
    setPublishError(null);
    try {
      await publishShapeRecipe(templateId);
    } catch (err: any) {
      setPublishError(err.message || "Failed to publish recipe");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleViewVersion = (version: number) => setViewingVersion(version);

  const handleActivateVersion = async () => {
    if (!templateId || confirmActivateVersion === null) return;
    setIsActivating(true);
    setActivateError(null);
    try {
      await activateShapeRecipeVersion(templateId, confirmActivateVersion);
      setConfirmActivateVersion(null);
    } catch (err: any) {
      setActivateError(err.message || "Failed to activate version");
    } finally {
      setIsActivating(false);
    }
  };

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!templateId) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteShapeRecipe(templateId);
      navigate(adminRoutes.documentRecipes);
    } catch (err: any) {
      console.error("Failed to delete template:", err);
      setDeleteError(err.message || t("naraShape.detail.errors.deleteFailed"));
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch projects when reference section expands
  const fetchRefProjects = async () => {
    setRefProjectsLoading(true);
    try {
      const wsId = getCurrentWorkspaceId() || "";
      const result = await getProjects(wsId);
      setRefProjects((result.projects || result || []).map((p: any) => ({ id: p.id, name: p.name })));
    } catch {
      setRefProjects([]);
    } finally {
      setRefProjectsLoading(false);
    }
  };

  // Fetch documents for selected project
  const fetchRefDocuments = async (projectId: string) => {
    setRefDocsLoading(true);
    try {
      const wsId = getCurrentWorkspaceId() || "";
      const result = await getProjectDocuments(wsId, projectId);
      setRefDocuments((result.documents || result || []).map((d: any) => ({ id: d.id, name: d.name, files: d.files })));
    } catch {
      setRefDocuments([]);
    } finally {
      setRefDocsLoading(false);
    }
  };

  // Extract structure from a document
  const handleExtractStructure = async (documentId: string) => {
    setExtractingDocId(documentId);
    setExtractError(null);
    setExtractedDefinition(null);
    try {
      const response = await adminExtractDocumentStructure(documentId, getCurrentWorkspaceId() || "");
      if (response.document_definition) {
        setExtractedDefinition(response.document_definition);
      } else {
        setExtractError("No document definition returned");
      }
    } catch (err: any) {
      setExtractError(err.message || "Failed to extract structure");
    } finally {
      setExtractingDocId(null);
    }
  };

  // Apply extracted definition as recipe schema
  const handleApplySchema = async () => {
    if (!templateId || !extractedDefinition) return;
    setIsApplyingSchema(true);
    try {
      await updateShapeRecipe(templateId, { schema: extractedDefinition as unknown as Record<string, unknown> });
    } catch (err: any) {
      setExtractError(err.message || "Failed to apply schema");
    } finally {
      setIsApplyingSchema(false);
    }
  };

  const decodeTemplateCode = (code?: string) => {
    if (!code) return t("naraShape.detail.typstCode.noCode");
    try {
      const binaryStr = atob(code);
      const bytes = Uint8Array.from(binaryStr, c => c.charCodeAt(0));
      return new TextDecoder('utf-8').decode(bytes);
    } catch (e) {
      return code;
    }
  };

  // Schema variable analysis
  const SYSTEM_DATA_FIELDS = new Set([
    "metadata", "sections", "story_payload", "files", "file_map",
    "custom_data", "name", "workspace_name", "project_name",
    "created_at", "updated_at", "created_by", "created_by_name",
    "ungrouped_images"
  ]);

  const getSchemaVariables = () => {
    const schema = templateDetails?.schema as Record<string, any> | undefined;
    if (!schema) return { generated: [], system: [] };

    // Handle document_definition format (from reference PDF extraction)
    if (schema.sections && Array.isArray(schema.sections)) {
      const generated: { path: string; inPrompt: boolean }[] = [];
      const promptText = isEditing ? editPrompt : (templateDetails?.prompt || "");
      const promptLower = promptText.toLowerCase();

      const extractFieldPaths = (sections: any[], prefix = "") => {
        for (const section of sections) {
          const sectionPath = prefix ? `${prefix}.${section.name}` : section.name;
          if (section.fields) {
            for (const field of section.fields) {
              const fieldPath = `${sectionPath}.${field.name}`;
              const inPrompt = promptLower.includes(field.name.toLowerCase()) ||
                promptLower.includes(field.name.replace(/_/g, " ").toLowerCase());
              generated.push({ path: fieldPath, inPrompt });
            }
          }
          if (section.subsections) {
            extractFieldPaths(section.subsections, sectionPath);
          }
        }
      };

      extractFieldPaths(schema.sections);
      return { generated, system: [] };
    }

    // Handle flat_fields format (from auto-extraction)
    const flatFields: string[] = schema.flat_fields || [];
    const promptText = isEditing ? editPrompt : (templateDetails?.prompt || "");
    const promptLower = promptText.toLowerCase();

    const generated: { path: string; inPrompt: boolean }[] = [];
    const system: string[] = [];

    for (const field of flatFields) {
      const path = field.replace(/^data\./, "");
      const topLevelKey = path.split(".")[0].replace(/\[\]$/, "");

      if (SYSTEM_DATA_FIELDS.has(topLevelKey)) {
        system.push(path);
        continue;
      }

      // Check if any part of the variable name appears in the prompt
      const parts = path.split(".").map((p: string) => p.replace(/\[\]$/, ""));
      const lastPart = parts[parts.length - 1];
      // Check for the variable name and common variations (underscores → spaces)
      const variations = [
        lastPart.toLowerCase(),
        lastPart.replace(/_/g, " ").toLowerCase(),
        lastPart.replace(/_/g, "").toLowerCase(),
      ];
      const inPrompt = variations.some((v) => promptLower.includes(v));

      generated.push({ path, inPrompt });
    }

    return { generated, system };
  };

  // Redirect non-admins to dashboard (after every hook: a hook below a return breaks the rules of hooks)
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={urls?.dashboard || "/dashboard"} replace />;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-48 bg-muted rounded animate-pulse" />
        <div className="h-6 w-96 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  // Error state
  if (error || !templateDetails) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium text-foreground mb-2">
          {t("naraShape.detail.templateNotFound")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {error || t("naraShape.detail.templateNotFoundDescription")}
        </p>
        <Link
          to={adminRoutes.documentRecipes}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("naraShape.detail.backToTemplates")}
        </Link>
      </div>
    );
  }

  // An extraction recipe reads products from files: no code, no document template, nothing to render.
  const isExtraction = (isEditing ? editInputMode : templateDetails.input_mode) === "extraction";

  return (
    <div>
      <PageHeader
        title={templateDetails.name || t("naraShape.detail.name")}
        isAdmin
        breadcrumbs={[
          { label: t("naraShape.breadcrumbs.admin") },
          { label: t("naraShape.breadcrumbs.documentRecipes"), href: adminRoutes.documentRecipes },
          { label: templateDetails.name || templateId || t("naraShape.detail.name") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  {t("naraShape.detail.cancel")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !editLabel.trim() || (!isExtraction && !editCode.trim())}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t("naraShape.detail.saving")}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Draft
                    </>
                  )}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex items-center justify-center p-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!isExtraction && (
                    <DropdownMenuItem
                      onClick={handleValidate}
                      disabled={isValidating || !editCode.trim()}
                    >
                      {isValidating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      {isValidating ? t("naraShape.detail.validating") : t("naraShape.detail.validate")}
                    </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {/* Published badge or Publish button */}
                {templateDetails.status === "published" ? (
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300">
                    Published
                  </span>
                ) : (
                  <button
                    onClick={handlePublish}
                    disabled={isPublishing}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {isPublishing ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Tag className="h-4 w-4" />
                        Publish
                      </>
                    )}
                  </button>
                )}
                {isAdmin && (
                  <>
                    <button
                      onClick={startEditing}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                      {t("naraShape.detail.edit")}
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="inline-flex items-center justify-center p-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={handleDuplicate}
                          disabled={isDuplicating}
                        >
                          <Copy className="h-4 w-4" />
                          {isDuplicating ? t("naraShape.detail.duplicate.duplicating") : t("naraShape.detail.duplicate.button")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="space-y-6">
        {/* Save Error */}
        {saveError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{saveError}</span>
          </div>
        )}

        {/* Publish Error */}
        {publishError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{publishError}</span>
          </div>
        )}

        {/* Validation Success */}
        {validationSuccess && !validationError && (
          <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{t("naraShape.detail.validationPassed")}</span>
          </div>
        )}

        {/* Validation Error/Warning */}
        {validationError && (
          <div className={`p-3 rounded-lg border ${
            validationError.is_data_error
              ? "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300"
              : "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
          }`}>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                {validationError.is_data_error
                  ? t("naraShape.detail.errors.dataWarning")
                  : t("naraShape.detail.errors.syntaxError")}
              </span>
            </div>
            <p className="text-sm mt-1 ml-7">{validationError.message}</p>
            {validationError.diagnostic && (
              <div className="mt-2 ml-7">
                <button
                  onClick={() => setShowDiagnostic(!showDiagnostic)}
                  className="text-xs underline opacity-80 hover:opacity-100"
                >
                  {t("naraShape.detail.errors.showDiagnostic")}
                </button>
                {showDiagnostic && (
                  <pre className="mt-2 p-3 text-xs font-mono bg-black/10 dark:bg-white/10 rounded overflow-x-auto whitespace-pre-wrap">
                    {validationError.diagnostic}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}

        {/* Template Information */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-lg font-medium text-foreground">{t("naraShape.detail.information.title")}</h3>
            <p className="text-sm text-muted-foreground">{t("naraShape.detail.information.description")}</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("naraShape.detail.id")}</label>
                <div className="flex items-center h-10 px-3 border border-border rounded-lg bg-muted text-muted-foreground">
                  <code className="font-mono text-sm">{templateId}</code>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("naraShape.detail.idHint")}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("naraShape.detail.name")}</label>
                <div className="flex items-center h-10 px-3 border border-border rounded-lg bg-muted">
                  {templateDetails.name || "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("naraShape.detail.nameHint")}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("naraShape.detail.inputMode")}</label>
                {isEditing ? (
                  <select
                    value={editInputMode}
                    onChange={(e) => {
                      setEditInputMode(e.target.value);
                      setSchemaErrorKey(null);
                    }}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-card"
                  >
                    <option value="transcription">{t("naraShape.templateInputMode.transcription")}</option>
                    <option value="project">{t("naraShape.templateInputMode.project")}</option>
                    <option value="extraction">{t("naraShape.templateInputMode.extraction")}</option>
                  </select>
                ) : (
                  <div className="flex items-center h-10 px-3 border border-border rounded-lg bg-muted">
                    <RecipeModeBadge mode={templateDetails.input_mode} />
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">{t(isExtraction ? "naraShape.detail.extractionHint" : "naraShape.detail.inputModeHint")}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("naraShape.detail.locale")}</label>
                {isEditing ? (
                  <select
                    value={editLocale}
                    onChange={(e) => setEditLocale(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-card"
                  >
                    <option value="">{t("naraShape.detail.localeDefault")}</option>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                    <option value="de">Deutsch</option>
                    <option value="it">Italiano</option>
                    <option value="pt">Português</option>
                    <option value="nl">Nederlands</option>
                  </select>
                ) : (
                  <div className="flex items-center h-10 px-3 border border-border rounded-lg bg-muted">
                    {templateDetails.locale || t("naraShape.detail.localeDefault")}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">{t("naraShape.detail.localeHint")}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("naraShape.detail.templateDescription")}</label>
              {isEditing ? (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder={t("naraShape.detail.descriptionPlaceholder")}
                  className="w-full h-20 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              ) : (
                <div className="px-3 py-2 border border-border rounded-lg bg-muted min-h-[80px]">
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {templateDetails.description || t("naraShape.detail.noDescriptionValue")}
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {t("naraShape.detail.descriptionHint")}
              </p>
            </div>

            {templateDetails.created_at && (
              <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                {t("naraShape.detail.created")} {formatDate(templateDetails.created_at, "long-datetime")}
              </div>
            )}
          </div>
        </div>

        {isExtraction && (
          <ProductSchemaSection
            schema={isEditing ? undefined : (templateDetails.schema as Record<string, unknown> | undefined)}
            isEditing={isEditing}
            text={editSchema}
            onChange={(text) => {
              setEditSchema(text);
              setSchemaErrorKey(null);
            }}
            errorKey={schemaErrorKey}
          />
        )}

        {/* Typst Code */}
        {!isExtraction && (
        <div className="bg-card rounded-lg border border-border">
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-medium text-foreground">{t("naraShape.detail.typstCode.title")}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{t("naraShape.detail.typstCode.description")}</p>
          </div>
          <div className="p-4">
            {isEditing ? (
              <textarea
                value={editCode}
                onChange={(e) => {
                  setEditCode(e.target.value);
                  setValidationError(null);
                  setValidationSuccess(false);
                }}
                placeholder={t("naraShape.detail.typstCode.placeholder")}
                className="w-full h-96 px-3 py-2 font-mono text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            ) : (
              <div className="bg-muted rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm font-mono whitespace-pre-wrap">
                  {decodeTemplateCode(templateDetails.code)}
                </pre>
              </div>
            )}
          </div>
        </div>

        )}

        {/* AI Prompt */}
        <div className="bg-card rounded-lg border border-border">
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-medium text-foreground">{t("naraShape.detail.aiPrompt.title")}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{t("naraShape.detail.aiPrompt.description")}</p>
          </div>
          <div className="p-4">
            {isEditing ? (
              <textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                placeholder={t("naraShape.detail.aiPrompt.placeholder")}
                className="w-full h-48 px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            ) : (
              <div className="bg-muted rounded-lg p-4">
                <pre className="text-sm whitespace-pre-wrap">
                  {templateDetails.prompt || t("naraShape.detail.aiPrompt.noPrompt")}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Template Variables */}
        {!isExtraction && (
        <div className="bg-card rounded-lg border border-border">
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-medium text-foreground">{t("naraShape.detail.schema.title")}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{t("naraShape.detail.schema.description")}</p>
          </div>
          <div className="p-4 space-y-4">
            {templateDetails.schema ? (
              (() => {
                const { generated, system } = getSchemaVariables();
                const uncoveredCount = generated.filter((v) => !v.inPrompt).length;
                return (
                  <>
                    {/* Generated Variables */}
                    {generated.length > 0 ? (
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                          {t("naraShape.detail.schema.generatedVars")}
                          {uncoveredCount > 0 && (
                            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300">
                              {t("naraShape.detail.schema.uncoveredCount", { count: uncoveredCount })}
                            </span>
                          )}
                        </h4>
                        <div className="space-y-1">
                          {generated.map((v) => (
                            <div
                              key={v.path}
                              className="flex items-center gap-2 px-3 py-1.5 rounded text-sm font-mono"
                            >
                              <span className={v.inPrompt ? "text-green-600 dark:text-green-400" : "text-amber-500 dark:text-amber-400"}>
                                {v.inPrompt ? "\u2713" : "?"}
                              </span>
                              <span className="text-foreground">{v.path}</span>
                              {!v.inPrompt && (
                                <span className="text-xs text-muted-foreground ml-auto">
                                  {t("naraShape.detail.schema.notInPrompt")}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t("naraShape.detail.schema.noGeneratedVars")}
                      </p>
                    )}

                    {/* System Variables (collapsed) */}
                    {system.length > 0 && (
                      <details className="text-sm">
                        <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                          {t("naraShape.detail.schema.systemVars", { count: system.length })}
                        </summary>
                        <div className="mt-2 space-y-1 pl-2">
                          {system.map((path) => (
                            <div key={path} className="text-xs font-mono text-muted-foreground px-3 py-1">
                              {path}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Raw Schema (collapsed) */}
                    <details className="text-sm">
                      <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                        {t("naraShape.detail.schema.rawSchema")}
                      </summary>
                      <div className="mt-2 bg-muted rounded-lg p-4">
                        <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                          {JSON.stringify(templateDetails.schema, null, 2)}
                        </pre>
                      </div>
                    </details>
                  </>
                );
              })()
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("naraShape.detail.schema.noSchema")}
              </p>
            )}
          </div>
        </div>

        )}

        {/* Reference Structure */}
        {!isExtraction && (
        <div className="bg-card rounded-lg border border-border">
          <button
            onClick={() => {
              const next = !refExpanded;
              setRefExpanded(next);
              if (next && refProjects.length === 0) {
                fetchRefProjects();
              }
            }}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-accent/50 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-medium text-foreground">Reference Structure</h3>
            </div>
            {refExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          {refExpanded && (
            <div className="px-4 pb-4 border-t border-border space-y-4">
              <p className="text-sm text-muted-foreground pt-3">
                Select a project containing reference PDFs, then extract the document structure to use as this recipe's schema.
              </p>

              {/* Project Selector */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Reference Project</label>
                {refProjectsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading projects...
                  </div>
                ) : (
                  <select
                    value={refProjectId || ""}
                    onChange={(e) => {
                      const pid = e.target.value || null;
                      setRefProjectId(pid);
                      setRefDocuments([]);
                      setExtractedDefinition(null);
                      setExtractError(null);
                      if (pid) fetchRefDocuments(pid);
                    }}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-card"
                  >
                    <option value="">Select a project...</option>
                    {refProjects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Document List */}
              {refProjectId && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Documents</label>
                  {refDocsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Loading documents...
                    </div>
                  ) : refDocuments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No documents in this project.</p>
                  ) : (
                    <div className="space-y-2">
                      {refDocuments.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between px-3 py-2 border border-border rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{doc.name}</span>
                          </div>
                          <button
                            onClick={() => handleExtractStructure(doc.id)}
                            disabled={extractingDocId !== null}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            {extractingDocId === doc.id ? (
                              <>
                                <RefreshCw className="h-3 w-3 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Search className="h-3 w-3" />
                                Extract Structure
                              </>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Extract Error */}
              {extractError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{extractError}</span>
                </div>
              )}

              {/* Extracted Definition Preview */}
              {extractedDefinition && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-foreground">Extracted Structure</h4>
                    <button
                      onClick={handleApplySchema}
                      disabled={isApplyingSchema}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {isApplyingSchema ? (
                        <>
                          <RefreshCw className="h-3 w-3 animate-spin" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <Download className="h-3 w-3" />
                          Apply to Recipe
                        </>
                      )}
                    </button>
                  </div>

                  {/* Document type and description */}
                  <div className="bg-muted rounded-lg p-3 space-y-2">
                    <div>
                      <span className="text-xs font-medium text-muted-foreground uppercase">Document Type</span>
                      <p className="text-sm font-medium">{extractedDefinition.document_type}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground uppercase">Description</span>
                      <p className="text-sm">{extractedDefinition.description}</p>
                    </div>
                  </div>

                  {/* Sections tree */}
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase">Sections</span>
                    {extractedDefinition.sections.map((section, i) => (
                      <SectionPreview key={i} section={section} />
                    ))}
                  </div>

                  {/* Raw definition (collapsed) */}
                  <details className="text-sm">
                    <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                      Raw Definition
                    </summary>
                    <div className="mt-2 bg-muted rounded-lg p-4">
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                        {JSON.stringify(extractedDefinition, null, 2)}
                      </pre>
                    </div>
                  </details>
                </div>
              )}
            </div>
          )}
        </div>

        )}

        {/* Version History */}
        <div className="bg-card rounded-lg border border-border">
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-accent/50 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-medium text-foreground">Version History</h3>
              {templateDetails.active_version != null && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300">
                  v{templateDetails.active_version} active
                </span>
              )}
            </div>
            {historyExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          {historyExpanded && (
            <div className="px-4 pb-4 border-t border-border">
              {versionsLoading ? (
                <div className="py-6 text-center text-muted-foreground">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading versions...
                </div>
              ) : versionsQuery.isError ? (
                <div className="py-6 text-center text-red-500 text-sm">{t("naraShape.detail.errors.versionsFailed")}</div>
              ) : versions.length === 0 ? (
                <div className="py-6 text-center text-muted-foreground text-sm">
                  No published versions yet. Click "Publish" to create the first version.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {versions.map((v) => (
                    <div key={v.version} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-medium">v{v.version}</span>
                        {v.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 font-medium">
                            active
                          </span>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {new Date(v.published_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewVersion(v.version)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-foreground border border-border rounded hover:bg-accent transition-colors"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </button>
                        {!v.is_active && (
                          <button
                            onClick={() => setConfirmActivateVersion(v.version)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-foreground border border-border rounded hover:bg-accent transition-colors"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Activate
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Raw Data (collapsible) */}
        <details className="text-sm">
          <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
            {t("naraShape.detail.rawData")}
          </summary>
          <div className="mt-2 p-4 bg-muted rounded-lg font-mono text-xs">
            <pre className="whitespace-pre-wrap break-words">
              {JSON.stringify(templateDetails, null, 2)}
            </pre>
          </div>
        </details>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">
              {t("naraShape.detail.deleteDialog.title")}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t("naraShape.detail.deleteDialog.description", { name: templateDetails.name })}
            </p>
            {deleteError && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{deleteError}</span>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteError(null);
                }}
                disabled={isDeleting}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                {t("naraShape.detail.deleteDialog.cancel")}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? t("naraShape.detail.deleteDialog.deleting") : t("naraShape.detail.deleteDialog.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version Detail Dialog */}
      {viewingVersion !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                Version {viewingVersion}
                {viewingVersionData?.is_active && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 font-medium">
                    active
                  </span>
                )}
              </h2>
              <button
                onClick={() => setViewingVersion(null)}
                className="p-1 hover:bg-accent rounded transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {viewingVersionLoading ? (
              <div className="py-8 text-center text-muted-foreground">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                Loading version details...
              </div>
            ) : viewingVersionData ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Version {viewingVersion} — Typst Code</h3>
                  <div className="bg-muted rounded-lg p-3 overflow-x-auto max-h-64 overflow-y-auto">
                    <pre className="text-xs font-mono whitespace-pre-wrap">
                      {decodeTemplateCode(viewingVersionData.code)}
                    </pre>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Current Draft — Typst Code</h3>
                  <div className="bg-muted rounded-lg p-3 overflow-x-auto max-h-64 overflow-y-auto">
                    <pre className="text-xs font-mono whitespace-pre-wrap">
                      {decodeTemplateCode(templateDetails.code)}
                    </pre>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Version {viewingVersion} — Prompt</h3>
                  <div className="bg-muted rounded-lg p-3 max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {viewingVersionData.prompt || "No prompt"}
                    </pre>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Current Draft — Prompt</h3>
                  <div className="bg-muted rounded-lg p-3 max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {templateDetails.prompt || "No prompt"}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground text-sm">
                Failed to load version details.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activate Confirmation Dialog */}
      {confirmActivateVersion !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Activate Version {confirmActivateVersion}?
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              This will make version {confirmActivateVersion} the active version used for PDF generation. Your draft content will not be affected.
            </p>
            {activateError && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{activateError}</span>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setConfirmActivateVersion(null); setActivateError(null); }}
                disabled={isActivating}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleActivateVersion}
                disabled={isActivating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isActivating ? "Activating..." : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
